import React, { useState, useEffect, useCallback, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Table, Badge, Form, Pagination } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function DismissProductIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [products, setProducts] = useState([]);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationMeta, setPaginationMeta] = useState(null);

    // Dropdown Data States
    const [filterData, setFilterData] = useState({
        lister: [],
        issuer: [],
        checker: [],
        mplacer: [],
        brands: [],
        stores: []
    });

    // Selected Filter States
    const [search, setSearch] = useState("");
    const [selectedIssuer, setSelectedIssuer] = useState("");
    const [selectedLister, setSelectedLister] = useState("");
    const [selectedChecker, setSelectedChecker] = useState("");
    const [selectedMplacer, setSelectedMplacer] = useState("");
    const [selectedBrand, setSelectedBrand] = useState("");
    const [selectedStore, setSelectedStore] = useState("");

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";

    const resolveImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) {
            return path;
        }
        return `${imgBaseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };

    const fetchData = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("authToken");
            const headers = { Authorization: `Bearer ${token}` };

            const params = {
                page,
                ...(search && { search }),
                ...(selectedIssuer && { issuer_id: selectedIssuer }),
                ...(selectedLister && { lister_id: selectedLister }),
                ...(selectedChecker && { checker_id: selectedChecker }),
                ...(selectedMplacer && { mplacer_id: selectedMplacer }),
                ...(selectedBrand && { brand_id: selectedBrand }),
                ...(selectedStore && { store_id: selectedStore }),
            };

            const [productsRes, initialRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/admin/dismiss-products`, { headers, params }),
                axios.get(`${process.env.REACT_APP_API_URL}/admin/initial-data`, { headers }).catch(() => null)
            ]);

            if (initialRes && initialRes.data?.success) {
                setFilterData({
                    lister: initialRes.data.lister || [],
                    issuer: initialRes.data.issuer || [],
                    checker: initialRes.data.checker || [],
                    mplacer: initialRes.data.mplacer || [],
                    brands: initialRes.data.brands || [],
                    stores: initialRes.data.stores || []
                });
            }

            const responseData = productsRes.data;
            if (responseData && responseData.data) {
                setProducts(responseData.data);
                setPaginationMeta(responseData.meta || null);
            } else {
                setProducts([]);
                setPaginationMeta(null);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
            setError(err.response?.data?.message || "Failed to fetch required data.");
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [
        search,
        selectedIssuer,
        selectedLister,
        selectedChecker,
        selectedMplacer,
        selectedBrand,
        selectedStore
    ]);

    useEffect(() => {
        fetchData(currentPage);
    }, [fetchData, currentPage]);

    const handlePageChange = (page) => {
        if (page >= 1 && paginationMeta && page <= paginationMeta.last_page) {
            setCurrentPage(page);
        }
    };

    // Safe Helper Function to format Select Options
    const formatSelectOptions = (list) => {
        if (!Array.isArray(list)) return [];
        return list.map((item) => {
            const displayName = item.nickname || item.nick_name
                ? `${item.name} (${item.nickname || item.nick_name})`
                : item.name;

            return {
                value: String(item.id),
                label: displayName,
            };
        });
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: "38px",
            borderRadius: "0.375rem",
            borderColor: "#dee2e6",
        }),
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("Dismiss Products")} ({paginationMeta?.total || products.length})
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("Home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{t("Dismiss Products")}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4">
                                {error}
                            </Alert>
                        )}

                        <Row className="g-3 align-items-end mb-4">
                            <Col sm={6} md={4} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Search</Form.Label>
                                    <Form.Control
                                        type="search"
                                        placeholder="Search Code, Title..."
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={6} md={4} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Issuer</Form.Label>
                                    <Select
                                        isClearable
                                        isSearchable
                                        placeholder="All Issuers"
                                        styles={customSelectStyles}
                                        options={formatSelectOptions(filterData.issuer)}
                                        value={formatSelectOptions(filterData.issuer).find(opt => opt.value === selectedIssuer) || null}
                                        onChange={(opt) => {
                                            setSelectedIssuer(opt ? opt.value : "");
                                            setCurrentPage(1);
                                        }}
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={6} md={4} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Lister</Form.Label>
                                    <Select
                                        isClearable
                                        isSearchable
                                        placeholder="All Listers"
                                        styles={customSelectStyles}
                                        options={formatSelectOptions(filterData.lister)}
                                        value={formatSelectOptions(filterData.lister).find(opt => opt.value === selectedLister) || null}
                                        onChange={(opt) => {
                                            setSelectedLister(opt ? opt.value : "");
                                            setCurrentPage(1);
                                        }}
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={6} md={4} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Checker</Form.Label>
                                    <Select
                                        isClearable
                                        isSearchable
                                        placeholder="All Checkers"
                                        styles={customSelectStyles}
                                        options={formatSelectOptions(filterData.checker)}
                                        value={formatSelectOptions(filterData.checker).find(opt => opt.value === selectedChecker) || null}
                                        onChange={(opt) => {
                                            setSelectedChecker(opt ? opt.value : "");
                                            setCurrentPage(1);
                                        }}
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={6} md={4} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Market Placer</Form.Label>
                                    <Select
                                        isClearable
                                        isSearchable
                                        placeholder="All Market Placers"
                                        styles={customSelectStyles}
                                        options={formatSelectOptions(filterData.mplacer)}
                                        value={formatSelectOptions(filterData.mplacer).find(opt => opt.value === selectedMplacer) || null}
                                        onChange={(opt) => {
                                            setSelectedMplacer(opt ? opt.value : "");
                                            setCurrentPage(1);
                                        }}
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={6} md={4} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Brand</Form.Label>
                                    <Select
                                        isClearable
                                        isSearchable
                                        placeholder="All Brands"
                                        styles={customSelectStyles}
                                        options={formatSelectOptions(filterData.brands)}
                                        value={formatSelectOptions(filterData.brands).find(opt => opt.value === selectedBrand) || null}
                                        onChange={(opt) => {
                                            setSelectedBrand(opt ? opt.value : "");
                                            setCurrentPage(1);
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="danger" />
                                <p className="mt-2 text-muted">{t("Loading dismiss products...")}</p>
                            </div>
                        ) : (
                            <>
                                <div className="mc-table-responsive">
                                    <Table hover className="mc-table align-middle">
                                        <thead className="mc-table-head primary">
                                            <tr>
                                                <th>Product</th>
                                                <th>Code</th>
                                                <th>Selling Price</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="mc-table-body even">
                                            {products.length > 0 ? (
                                                products.map((product) => {
                                                    const cardImgPath = product.img?.card_img || product.img?.first_img;
                                                    const statusText = product.status || product.workflow_status || "DISMISSED";

                                                    return (
                                                        <tr key={product.id}>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {cardImgPath && (
                                                                        <img
                                                                            src={resolveImageUrl(cardImgPath)}
                                                                            alt={product.title}
                                                                            style={{
                                                                                width: "45px",
                                                                                height: "45px",
                                                                                objectFit: "cover",
                                                                                borderRadius: "6px"
                                                                            }}
                                                                        />
                                                                    )}
                                                                    <span className="fw-semibold text-truncate" style={{ maxWidth: "200px" }}>
                                                                        {product.title || "N/A"}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td>{product.product_code || "N/A"}</td>
                                                            <td className="fw-bold">
                                                                ${product.pricing?.sell_price_dollar || product.sell_price_dollar || "0.00"}
                                                            </td>
                                                            <td>
                                                                <Badge
                                                                    bg="danger"
                                                                    className="text-truncate px-2 py-1"
                                                                    style={{
                                                                        maxWidth: "140px",
                                                                        display: "inline-block",
                                                                        overflow: "hidden",
                                                                        verticalAlign: "middle"
                                                                    }}
                                                                    title={statusText}
                                                                >
                                                                  Dismiss By checker
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <div className="mc-table-action d-flex align-items-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        className="material-icons edit border-0 bg-transparent text-primary"
                                                                        onClick={() => navigate(`/products/detail`, { state: { product } })}
                                                                        title="View Details"
                                                                    >
                                                                        visibility
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className="text-center py-4">
                                                        {t("No dismissed products found")}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Pagination Controls */}
                                {paginationMeta && paginationMeta.last_page > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
                                        <span className="text-muted small">
                                            Showing {paginationMeta.from} to {paginationMeta.to} of {paginationMeta.total} results
                                        </span>
                                        <Pagination className="mb-0 flex-wrap">
                                            <Pagination.Prev
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            />

                                            {(() => {
                                                const totalPages = paginationMeta.last_page;
                                                const delta = 2;
                                                const range = [];
                                                const rangeWithDots = [];

                                                for (let i = 1; i <= totalPages; i++) {
                                                    if (
                                                        i === 1 ||
                                                        i === totalPages ||
                                                        (i >= currentPage - delta && i <= currentPage + delta)
                                                    ) {
                                                        range.push(i);
                                                    }
                                                }

                                                let l;
                                                for (let i of range) {
                                                    if (l) {
                                                        if (i - l === 2) {
                                                            rangeWithDots.push(l + 1);
                                                        } else if (i - l !== 1) {
                                                            rangeWithDots.push("...");
                                                        }
                                                    }
                                                    rangeWithDots.push(i);
                                                    l = i;
                                                }

                                                return rangeWithDots.map((page, idx) => {
                                                    if (page === "...") {
                                                        return <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />;
                                                    }

                                                    return (
                                                        <Pagination.Item
                                                            key={page}
                                                            active={page === currentPage}
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page}
                                                        </Pagination.Item>
                                                    );
                                                });
                                            })()}

                                            <Pagination.Next
                                                disabled={currentPage === paginationMeta.last_page}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            />
                                        </Pagination>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}